import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle2 } from "lucide-react";
import { useViewport } from "../../hooks/useViewport";
import Logo from "../../components/common/Logo";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { isDesktop } = useViewport();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate(`/${result.user.user_type}/dashboard`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  // Styles
  const containerStyle = { minHeight: "100vh", display: "flex" };
  const leftStyle = {
    width: "41.67%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundImage:
      "url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=1600&fit=crop)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative",
  };
  const overlayStyle = {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(22,163,74,0.9) 0%, rgba(21,128,61,0.95) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const rightStyle = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#f9fafb",
  };
  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: 20,
    boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  };
  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    border: "1.5px solid #e5e7eb",
    borderRadius: 12,
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
    backgroundColor: "transparent",
  };
  const btnPrimaryStyle = {
    backgroundColor: "#16a34a",
    color: "#fff",
    padding: "12px 24px",
    borderRadius: 12,
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    width: "100%",
    boxShadow: "0 2px 8px rgba(22,163,74,0.3)",
    transition: "transform 0.2s",
  };

  return (
    <div style={containerStyle}>
      {/* Left — Branding */}
      {isDesktop && (
        <div style={leftStyle}>
          <div style={overlayStyle}>
            <div
              style={{
                textAlign: "center",
                color: "#fff",
                padding: "0 40px",
                maxWidth: 450,
                animation: "slideInLeft 0.8s ease-out",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  marginBottom: 32,
                  animation: "float 4s ease-in-out infinite",
                }}
              >
                <Logo height={120} onDark showText={false} style={{ padding: "18px 24px", borderRadius: 20 }} />
              </div>
              {/* <div
                style={{
                  width: 64,
                  height: 1,
                  backgroundColor: "rgba(255,255,255,0.4)",
                  margin: "0 auto 32px",
                }}
              ></div> */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  margin: "0 auto 32px",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "2px",
                    background: "rgba(255,255,255,0.5)",
                  }}
                />

                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    background: "#d4a017",
                    transform: "rotate(45deg)",
                    borderRadius: "1px",
                  }}
                />

                <div
                  style={{
                    width: "70px",
                    height: "2px",
                    background: "rgba(255,255,255,0.5)",
                  }}
                />
              </div>
              <p
                style={{
                  color: "rgba(255,255,255,0.85)",
                  fontSize: 17,
                  lineHeight: 1.7,
                  marginBottom: 32,
                }}
              >
                Stay close to nature
              </p>
              <div
                style={{
                  textAlign: "left",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {[
                  "Easy online booking",
                  "Breathtaking Himalayan views",
                  "Guided trekking experiences",
                  "Cozy bonfire evenings",
                ].map((b) => (
                  <div key={b} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <CheckCircle2 size={18} color="rgba(255,255,255,0.9)" />
                    <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 13 }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right — Form */}
      <div style={rightStyle}>
        <div style={{ width: "100%", maxWidth: 450 }}>
          {/* Mobile Logo */}
          <div
            style={{
              display: isDesktop ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              marginBottom: 32,
            }}
          >
            <Logo height={48} />
          </div>

          <div style={cardStyle}>
            <div style={{ padding: 32 }}>
              <div style={{ textAlign: "center", marginBottom: 32 }}>
                <h2
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#1f2937",
                    marginBottom: 8,
                  }}
                >
                  Welcome Back
                </h2>
                <p style={{ color: "#6b7280", marginTop: 8, fontSize: 14 }}>
                  Sign in to your account to continue
                </p>
              </div>

              {error && (
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 24,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <AlertCircle size={18} color="#dc2626" />
                  <span style={{ fontSize: 13, color: "#991b1b" }}>
                    {error}
                  </span>
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                style={{ display: "flex", flexDirection: "column", gap: 20 }}
              >
                {/* Email */}
                <div>
                  <label
                    style={{
                      display: "block",
                      paddingBottom: 6,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#374151",
                      }}
                    >
                      Email Address
                    </span>
                  </label>
                  <div
                    className="input-row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      border: "1.5px solid #e5e7eb",
                      borderRadius: 12,
                      padding: "8px 14px",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <Mail size={16} color="#9ca3af" />
                    <input
                      type="email"
                      placeholder="Enter your email"
                      style={{
                        ...inputStyle,
                        border: "none",
                        padding: 0,
                        flex: 1,
                      }}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label
                    style={{
                      display: "block",
                      paddingBottom: 6,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#374151",
                      }}
                    >
                      Password
                    </span>
                  </label>
                  <div
                    className="input-row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      border: "1.5px solid #e5e7eb",
                      borderRadius: 12,
                      padding: "8px 14px",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <Lock size={16} color="#9ca3af" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      style={{
                        ...inputStyle,
                        border: "none",
                        padding: 0,
                        flex: 1,
                      }}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        color: "#9ca3af",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Options */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <label
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <input type="checkbox" style={{ accentColor: "#16a34a" }} />
                    <span style={{ fontSize: 13, color: "#6b7280" }}>
                      Remember me
                    </span>
                  </label>
                  <Link
                    to="/forgot-password"
                    style={{ fontSize: 13, color: "#16a34a", fontWeight: 500 }}
                  >
                    Forgot Password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{ ...btnPrimaryStyle, opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </button>
              </form>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "24px 0",
                  color: "#6b7280",
                  fontSize: 12,
                }}
              >
                <div
                  style={{ flex: 1, height: 1, backgroundColor: "#e5e7eb" }}
                ></div>
                <span style={{ padding: "0 16px" }}>or</span>
                <div
                  style={{ flex: 1, height: 1, backgroundColor: "#e5e7eb" }}
                ></div>
              </div>

              <p
                style={{ textAlign: "center", fontSize: 13, color: "#6b7280" }}
              >
                Don't have an account?{" "}
                <Link
                  to="/register"
                  style={{ color: "#16a34a", fontWeight: 600 }}
                >
                  Create Account
                </Link>
              </p>

              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Link to="/" style={{ fontSize: 13, color: "#9ca3af" }}>
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
